import React, { useState, useCallback, useRef } from 'react';
import { ChevronRight, Users, DollarSign, Target, Phone, CheckCircle, ArrowRight, Play, Calculator, Eye, LogOut } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Progress } from './components/ui/progress';
import './App.css';

// Countdown Timer Component
const CountdownTimer = React.memo(() => {
  const [timeLeft, setTimeLeft] = React.useState({ hours: 0, minutes: 0, seconds: 0 });

  React.useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const easternTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
      const midnight = new Date(easternTime);
      midnight.setHours(24, 0, 0, 0); // Next midnight (current day + 1 at 00:00:00)
      
      const difference = midnight - easternTime;
      
      if (difference > 0) {
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        
        setTimeLeft({ hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000); // Update every 1 second

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-red-900/30 border border-red-500 p-4 rounded-lg mt-2">
      <div className="text-center">
        <p className="text-red-300 text-sm mb-2">⏰ Special Pricing Expires At Midnight ET:</p>
        <div className="flex justify-center space-x-4 text-white">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400">{timeLeft.hours.toString().padStart(2, '0')}</div>
            <div className="text-xs">HOURS</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400">{timeLeft.minutes.toString().padStart(2, '0')}</div>
            <div className="text-xs">MINS</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400">{timeLeft.seconds.toString().padStart(2, '0')}</div>
            <div className="text-xs">SECS</div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Move step components outside to prevent re-rendering
const IntroStep = React.memo(({ nextStep }) => (
  <div className="text-center space-y-8">
    <h1 className="text-4xl font-bold text-white mb-4">
      Game Plan
    </h1>
    <p className="text-xl text-white mb-8">
      Get your personalized roadmap to <span className="text-yellow-400">full classes in 45 days</span>
    </p>
    
    <div className="bg-gray-900 p-6 rounded-lg mb-8">
      <h3 className="text-lg font-semibold text-white mb-4">Before we start...</h3>
      <p className="text-white">
        Did you watch our Demo of the Student Magnet+ Program that explains how martial arts schools 
        are getting <strong className="text-yellow-400">new students with ease</strong>?
      </p>
    </div>
    
    <div className="space-y-4">
      <Button onClick={nextStep} size="lg" className="w-full md:w-auto" style={{backgroundColor: '#52aef2'}}>
        Yes, I watched it - Let's Begin
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
      
      <div className="text-center">
        <a 
          href="https://getkaratestudents.com/video-e"
          target="_blank"
          rel="noopener noreferrer" 
          className="text-blue-400 hover:text-blue-300 underline text-sm"
        >
          Watch the demo now
        </a>
      </div>
    </div>
  </div>
));

const IsolatedInput = React.memo(({ placeholder, type, onBlur, className, defaultValue, tabIndex }) => {
  const [value, setValue] = React.useState(defaultValue || '');
  
  const handleChange = (e) => {
    setValue(e.target.value);
  };
  
  const handleBlur = () => {
    onBlur(value);
  };
  
  return (
    <input
      type={type}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      className={`${className} bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full`}
      placeholder={placeholder}
      tabIndex={tabIndex}
    />
  );
});

const IsolatedTextarea = React.memo(({ placeholder, onBlur, className, rows, defaultValue, tabIndex }) => {
  const [value, setValue] = React.useState(defaultValue || '');
  
  const handleChange = (e) => {
    setValue(e.target.value);
  };
  
  const handleBlur = () => {
    onBlur(value);
  };
  
  return (
    <textarea
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      className={`${className} bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full resize-none`}
      placeholder={placeholder}
      rows={rows}
      tabIndex={tabIndex}
    />
  );
});

const CurrentSituationStep = React.memo(({ localInputs, updateLocalInput, nextStep }) => {
  // Create stable callback references to prevent re-renders
  const updateCurrentStudents = React.useCallback((value) => updateLocalInput('current_students', value), [updateLocalInput]);
  const updateMonthlyRate = React.useCallback((value) => updateLocalInput('monthly_rate', value), [updateLocalInput]);
  const updateMarketingStrategy = React.useCallback((value) => updateLocalInput('marketing_strategy', value), [updateLocalInput]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Tell Us About Your School</h2>
        <p className="text-white">Help us understand your current situation</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-white">How many students do you currently have?</Label>
          <IsolatedInput
            type="number"
            defaultValue={localInputs.current_students}
            onBlur={updateCurrentStudents}
            className="mt-2"
            placeholder="e.g. 45"
            tabIndex={1}
          />
        </div>
        
        <div>
          <Label className="text-white">What's your monthly tuition rate?</Label>
          <IsolatedInput
            type="number"
            defaultValue={localInputs.monthly_rate}
            onBlur={updateMonthlyRate}
            className="mt-2"
            placeholder="e.g. 155"
            tabIndex={2}
          />
        </div>
      </div>
      
      <div>
        <Label className="text-white">What's your current marketing strategy?</Label>
        <IsolatedTextarea
          defaultValue={localInputs.marketing_strategy}
          onBlur={updateMarketingStrategy}
          className="mt-2"
          placeholder="e.g. Social media posts, referrals, community events..."
          rows={3}
          tabIndex={3}
        />
      </div>

      <Button 
        onClick={nextStep} 
        className="w-full"
        style={{backgroundColor: '#52aef2'}}
        disabled={!localInputs.current_students || !localInputs.monthly_rate}
      >
        Continue
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
});

const App = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(5);
  const [sessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));

  // Check URL for admin paths
  React.useEffect(() => {
    const path = window.location.pathname;
    if (path === '/admin-login') {
      setIsAdminLogin(true);
    } else if (path === '/admin-dashboard') {
      setIsAdminMode(true);
    }
  }, []);

  const [sessionData, setSessionData] = useState({
    name: '',
    phone: '',
    current_students: '',
    monthly_revenue: '',
    goal_students: '',
    goal_revenue: '',
    monthly_rate: '',
    marketing_strategy: '',
    current_cost_per_student: '',
    motivation: '',
    pain_points: '',
    commitment_level: 5,
    bucket_choice: '',
    timeline: ''
  });

  // Local state for form inputs to prevent re-rendering issues
  const [localInputs, setLocalInputs] = useState({
    current_students: '',
    monthly_rate: '',
    marketing_strategy: '',
    pain_points: '',
    motivation: '',
    goal_students: '',
    name: '',
    phone: ''
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Simplified state updater that doesn't trigger re-renders
  // Personalized messaging based on user inputs
  const getPersonalizedMessage = useCallback(() => {
    const marketingStrategy = sessionData.marketing_strategy?.toLowerCase() || '';
    const bucketChoice = sessionData.bucket_choice;
    const painPoints = sessionData.pain_points?.toLowerCase() || '';
    
    let messages = [];
    
    // Marketing strategy-based messaging
    if (marketingStrategy.includes('social media') || marketingStrategy.includes('facebook') || marketingStrategy.includes('instagram')) {
      messages.push("We'll help you turn your social media posts into high-converting ads that actually bring in paying students instead of just likes and comments.");
    } else if (marketingStrategy.includes('agency') || marketingStrategy.includes('hired') || marketingStrategy.includes('company')) {
      messages.push("You'll save thousands on agency fees by launching ads with our proven system and finally taking control of your marketing.");
    } else if (marketingStrategy.includes('google') || marketingStrategy.includes('search')) {
      messages.push("We'll optimize your online presence and create targeted ads that capture people actively searching for martial arts training.");
    } else if (marketingStrategy.includes('referral') || marketingStrategy.includes('word of mouth')) {
      messages.push("We'll amplify your great reputation with targeted ads to reach people who don't know about your school yet but would love what you offer.");
    } else if (marketingStrategy.includes('community') || marketingStrategy.includes('events') || marketingStrategy.includes('demo')) {
      messages.push("We'll help you stop spending endless hours at community events and start getting students through proven online systems that work 24/7.");
    } else {
      messages.push("We'll create a complete marketing system that consistently brings in qualified leads who are ready to train.");
    }
    
    // Bucket choice messaging
    if (bucketChoice === 'bucket1') {
      messages.push("Since you've tried everything without success, our proven system will give you a completely fresh approach that actually works.");
    } else if (bucketChoice === 'bucket2') {
      messages.push("Since you want to speed things up, our step-by-step system will get you results in weeks instead of years of trial and error.");
    } else if (bucketChoice === 'bucket3') {
      messages.push("Since you want a proven system, you'll get our exact blueprint that's already helped hundreds of schools grow successfully.");
    }
    
    // Pain points messaging
    if (painPoints.includes('leads') || painPoints.includes('inquiries')) {
      messages.push("We'll solve your lead generation problem with ads that consistently bring in 20-30 qualified prospects every month.");
    } else if (painPoints.includes('convert') || painPoints.includes('close') || painPoints.includes('enrollment')) {
      messages.push("Our proven enrollment system will help you convert 80%+ of your appointments into paying students.");
    } else if (painPoints.includes('expensive') || painPoints.includes('money') || painPoints.includes('cost')) {
      messages.push("Our system will actually save you money by eliminating wasteful marketing spend and focusing only on what works.");
    }
    
    return messages.slice(0, 3).join(' '); // Limit to 3 key points
  }, [sessionData.marketing_strategy, sessionData.bucket_choice, sessionData.pain_points]);

  const updateLocalInput = useCallback((field, value) => {
    setLocalInputs(prev => {
      const newState = { ...prev, [field]: value };
      return newState;
    });
    setSessionData(prev => {
      const newState = { ...prev, [field]: value };
      return newState;
    });
  }, []);

  const updateSessionData = useCallback((newData) => {
    setSessionData(prevData => ({ ...prevData, ...newData }));
  }, []);

  const saveSessionData = useCallback(async (data) => {
    try {
      // Convert numeric fields to integers before sending to backend
      const dataToSend = { ...data };
      const numericFields = ['current_students', 'monthly_revenue', 'goal_students', 'goal_revenue', 'monthly_rate', 'current_cost_per_student', 'commitment_level'];
      
      numericFields.forEach(field => {
        if (dataToSend[field] && dataToSend[field] !== '') {
          const numValue = parseInt(dataToSend[field]);
          if (!isNaN(numValue)) {
            dataToSend[field] = numValue;
          }
        }
      });
      
      await fetch(`${API_BASE_URL}/api/strategy-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          ...dataToSend
        })
      });
    } catch (error) {
      console.error('Error saving session data:', error);
    }
  }, [API_BASE_URL, sessionId]);

  const nextStep = useCallback(() => {
    // We'll calculate steps.length dynamically since steps is defined later
    const totalSteps = 8; // We know we have 8 steps
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      setProgress(Math.round(((newStep + 1) / totalSteps) * 100));
      // Save data when moving to next step
      saveSessionData(sessionData);
      // Scroll to top when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, saveSessionData, sessionData]);

  const calculateROI = useCallback(() => {
    const students = parseInt(localInputs.current_students || sessionData.current_students) || 0;
    const goalStudents = parseInt(localInputs.goal_students || sessionData.goal_students) || 0;
    const monthlyRate = parseInt(localInputs.monthly_rate || sessionData.monthly_rate) || 155;
    const newStudents = goalStudents - students;
    const monthlyIncrease = newStudents * monthlyRate;
    const yearlyIncrease = monthlyIncrease * 12;
    
    return {
      newStudents,
      monthlyIncrease,
      yearlyIncrease,
      lifetime: newStudents * monthlyRate * 18 // 18 month average
    };
  }, [localInputs.current_students, localInputs.goal_students, localInputs.monthly_rate, sessionData.current_students, sessionData.goal_students, sessionData.monthly_rate]);

const PainPointsStep = React.memo(({ sessionData, localInputs, updateLocalInput, nextStep }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">What's Holding You Back?</h2>
        <p className="text-white">Let's identify the real challenges you're facing</p>
      </div>

      <div className="bg-red-900/20 border border-red-700 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-white mb-4">Current Monthly Revenue</h3>
        <div className="text-3xl font-bold text-yellow-400">
          ${(parseInt(sessionData.current_students) * parseInt(sessionData.monthly_rate)).toLocaleString() || '0'}*
        </div>
        <p className="text-white mt-2">
          {sessionData.current_students} students × ${sessionData.monthly_rate}/month
        </p>
      </div>

      <div>
        <Label className="text-white">What's your biggest marketing challenge right now?</Label>
        <IsolatedTextarea
          defaultValue={localInputs.pain_points}
          onBlur={(value) => updateLocalInput('pain_points', value)}
          className="mt-2"
          placeholder="e.g. Not getting enough leads, leads don't convert, marketing costs too much..."
          rows={4}
        />
      </div>

      <div>
        <Label className="text-white">What motivated you to look for a solution?</Label>
        <IsolatedTextarea
          defaultValue={localInputs.motivation}
          onBlur={(value) => updateLocalInput('motivation', value)}
          className="mt-2"
          placeholder="e.g. Need more income, want to grow my school, tired of struggling..."
          rows={3}
        />
      </div>

      <Button 
        onClick={nextStep} 
        className="w-full bg-primary hover:bg-primary/90"
        style={{backgroundColor: '#52aef2'}}
        disabled={!localInputs.pain_points || !localInputs.motivation}
      >
        Continue - Let's Set Your Goals
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
});

const GoalsStep = React.memo(({ localInputs, updateLocalInput, nextStep }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Where Do You Want To Be?</h2>
        <p className="text-white">Let's set your growth targets for the next 12 months</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Users className="mr-2 h-5 w-5" style={{color: '#52aef2'}} />
              Student Goal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Label className="text-white">How many students do you want?</Label>
            <IsolatedInput
              type="number"
              defaultValue={localInputs.goal_students}
              onBlur={(value) => updateLocalInput('goal_students', value)}
              className="mt-2"
              placeholder="e.g. 150"
            />
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <DollarSign className="mr-2 h-5 w-5" style={{color: '#52aef2'}} />
              Revenue Goal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-400">
              {(parseInt(localInputs.goal_students) && parseInt(localInputs.monthly_rate)) 
                ? `$${(parseInt(localInputs.goal_students) * parseInt(localInputs.monthly_rate)).toLocaleString()}/month`
                : "Enter a number above"}
            </div>
            <p className="text-white text-sm mt-2">Auto-calculated based on your goals</p>
          </CardContent>
        </Card>
      </div>

      {localInputs.goal_students && localInputs.current_students && (
        <div className="bg-green-900/20 border border-green-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-white mb-2">Your Growth Potential</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white">New Students Needed:</p>
              <p className="text-2xl font-bold text-yellow-400">
                {parseInt(localInputs.goal_students) - parseInt(localInputs.current_students)}
              </p>
            </div>
            <div>
              <p className="text-white">Additional Monthly Revenue:</p>
              <p className="text-2xl font-bold text-yellow-400">
                ${((parseInt(localInputs.goal_students) - parseInt(localInputs.current_students)) * parseInt(localInputs.monthly_rate)).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      <Button 
        onClick={nextStep} 
        className="w-full"
        style={{backgroundColor: '#52aef2'}}
        disabled={!localInputs.goal_students}
      >
        Set These Goals
        <Target className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
});

  const BucketsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Here's The Real Question...</h2>
        <p className="text-xl text-white mb-6">
          You want to go from <span className="text-yellow-400">{sessionData.current_students} students</span> to{' '}
          <span className="text-yellow-400">{sessionData.goal_students} students</span>...
        </p>
        <p className="text-2xl font-semibold text-white">
          What's stopping you from achieving that on your own?
        </p>
      </div>

      <p className="text-white text-center mb-8">
        Most school owners fall into one of these three categories. Which sounds most like you?
      </p>

      <div className="space-y-4">
        {[
          {
            id: 'bucket1',
            title: 'I\'ve tried everything',
            description: 'You\'ve done everything you can think of, but nothing seems to work. You\'re out of ideas and need a completely different approach.',
            color: 'border-red-500 bg-red-900/20'
          },
          {
            id: 'bucket2', 
            title: 'I could figure it out, but...',
            description: 'You probably could figure it out eventually, but it would take too much time. You want to speed things up and get there faster.',
            color: 'border-yellow-500 bg-yellow-900/20'
          },
          {
            id: 'bucket3',
            title: 'I want a proven system',
            description: 'You could come up with different ways to do it, but you want someone who has already done it to teach you exactly how.',
            color: 'border-green-500 bg-green-900/20'
          }
        ].map((bucket) => (
          <Card 
            key={bucket.id}
            className={`cursor-pointer transition-all ${bucket.color} ${
              sessionData.bucket_choice === bucket.id ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => updateSessionData({ bucket_choice: bucket.id })}
          >
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold text-white mb-2">{bucket.title}</h3>
              <p className="text-white">{bucket.description}</p>
              {sessionData.bucket_choice === bucket.id && (
                <div className="flex justify-end mt-2">
                  <CheckCircle className="w-6 h-6" style={{color: '#52aef2'}} />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button 
        onClick={nextStep} 
        className="w-full"
        style={{backgroundColor: '#52aef2'}}
        disabled={!sessionData.bucket_choice}
      >
        That's Me - Continue
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );

const ContactStep = React.memo(({ sessionData, localInputs, updateLocalInput, nextStep, getPersonalizedMessage }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Perfect! I Can Help You</h2>
        <p className="text-white mb-6">
          Based on what you've shared, I'm confident we can get you to{' '}
          <span className="text-yellow-400">{sessionData.goal_students} students</span>.
        </p>
      </div>

      <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg mb-6">
        <h3 className="text-xl font-semibold text-white mb-4">Here's How I'll Help You Specifically:</h3>
        <p className="text-white leading-relaxed">
          {getPersonalizedMessage()}
        </p>
      </div>

      <div className="bg-primary/20 border border-primary p-6 rounded-lg mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">What You'll Get Next:</h3>
        <ul className="text-white space-y-2">
          <li>✓ Your personalized ROI calculation</li>
          <li>✓ The exact 3-step system we use</li>
          <li>✓ Timeline to reach your goals</li>
          <li>✓ Investment details and special pricing</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-white">First Name *</Label>
          <IsolatedInput
            defaultValue={localInputs.name}
            onBlur={(value) => updateLocalInput('name', value)}
            className="mt-2"
            placeholder="Your first name"
          />
        </div>
        
        <div>
          <Label className="text-white">Phone Number *</Label>
          <IsolatedInput
            defaultValue={localInputs.phone}
            onBlur={(value) => updateLocalInput('phone', value)}
            className="mt-2"
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <Button 
        onClick={nextStep} 
        className="w-full"
        style={{backgroundColor: '#52aef2'}}
        disabled={!localInputs.name || !localInputs.phone}
      >
        Show Me The System
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
});

  const SolutionStep = () => {
    const roi = calculateROI();
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Here's Your Personalized Roadmap, {sessionData.name}
          </h2>
          <p className="text-white">
            The exact system to get you from <span className="text-yellow-400">{sessionData.current_students}</span> to{' '}
            <span className="text-yellow-400">{sessionData.goal_students} students</span>
          </p>
        </div>

        <Card className="bg-gradient-to-r from-primary/20 to-yellow-500/20 border-primary">
          <CardHeader>
            <CardTitle className="text-white text-center flex items-center justify-center">
              <Calculator className="mr-2 h-6 w-6" />
              Your ROI Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white">New Students Needed:</p>
                <p className="text-3xl font-bold text-yellow-400">{roi.newStudents}</p>
              </div>
              <div>
                <p className="text-white">Additional Monthly Revenue:</p>
                <p className="text-3xl font-bold text-yellow-400">${roi.monthlyIncrease.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-white">First Year Increase:</p>
                <p className="text-3xl font-bold text-yellow-400">${roi.yearlyIncrease.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-white">Lifetime Value:</p>
                <p className="text-3xl font-bold text-green-400">${roi.lifetime.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="bg-gray-900 p-6 rounded-lg">
          <h3 className="text-2xl font-bold text-white mb-4">The Student Magnet+ System</h3>
          <p className="text-white mb-6">
            Here are the 3 things we need to nail down to get you to {sessionData.goal_students} students:
          </p>
          
          <div className="space-y-4">
            {[
              {
                step: 1,
                title: 'Build an Effective Ad Account',
                description: 'Using our simple-click system, you\'ll launch ads that are pre-set to target the exact right people ready to enroll. No more wasting money on boosting posts or wrong audiences.'
              },
              {
                step: 2,
                title: 'Proven Lead Follow-Up System',
                description: 'Our Follow-Up Ninja system converts leads to appointments automatically, so you get a majority of leads scheduling instead of just some.'
              },
              {
                step: 3,
                title: 'Convert Appointments to Paying Students',
                description: 'Our proven process converts appointments into paying students from day one - no more free trials that never convert.'
              }
            ].map((item) => (
              <div key={item.step} className="flex items-start space-x-4">
                <div className="text-black rounded-full w-8 h-8 flex items-center justify-center font-bold" style={{backgroundColor: '#52aef2'}}>
                  {item.step}
                </div>
                <div>
                  <h4 className="text-white font-semibold">{item.title}</h4>
                  <p className="text-white text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-yellow-500/20 border border-yellow-500 p-6 rounded-lg">
          <h3 className="text-xl font-bold text-white mb-2">Conservative Numbers</h3>
          <p className="text-white">
            Even with conservative estimates of just 3 appointments per week and closing 2 out of 3,
            you'd add <strong className="text-yellow-400">${(parseInt(sessionData.monthly_rate || 155) * 2.5 * 4).toLocaleString()}*</strong> in monthly revenue in just your first month.
          </p>
        </div>

        <Button onClick={nextStep} className="w-full" size="lg" style={{backgroundColor: '#52aef2'}}>
          This Sounds Perfect - What's The Investment?
          <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    );
  };

  const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('admin_token', data.token);
          setAdminToken(data.token);
          window.location.href = '/admin-dashboard';
        } else {
          setError('Invalid credentials');
        }
      } catch (error) {
        setError('Login failed. Please try again.');
      }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="bg-gray-900 p-8 rounded-lg w-full max-w-md">
          <h1 className="text-3xl font-bold text-white text-center mb-8">Admin Login</h1>
          
          {error && (
            <div className="bg-red-900/30 border border-red-500 text-red-300 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-white block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                required
              />
            </div>
            
            <div>
              <label className="text-white block mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                required
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full"
              style={{backgroundColor: '#52aef2'}}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  };

  const AdminDashboard = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchSessions = useCallback(async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/sessions?page=${page}&limit=${limit}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setSessions(data.sessions || []);
          setTotal(data.total || 0);
          setTotalPages(data.total_pages || 0);
        } else if (response.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/admin-login';
        } else {
          // Handle other errors gracefully
          setSessions([]);
          setTotal(0);
          setTotalPages(0);
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
        setSessions([]);
        setTotal(0);
        setTotalPages(0);
      }
      setLoading(false);
    }, [page, limit, adminToken]);

    React.useEffect(() => {
      if (adminToken) {
        fetchSessions();
      } else {
        window.location.href = '/admin-login';
      }
    }, [fetchSessions, adminToken]);

    const handleLogout = () => {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin-login';
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    };

    // Show error message if no sessions and there was an error
    const showDatabaseMessage = sessions.length === 0 && !loading;

    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold">Student Magnet Survey Results</h1>
            <Button onClick={handleLogout} className="bg-red-600 hover:bg-red-700">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          {/* Database Status Message */}
          {showDatabaseMessage && (
            <div className="bg-blue-900/30 border border-blue-500 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold text-blue-300 mb-3">📊 Survey Results Status</h3>
              <p className="text-blue-200 mb-3">
                The admin dashboard is working, but no survey submissions are currently visible due to database collection permissions.
              </p>
              <div className="text-sm text-blue-300 space-y-2">
                <p><strong>What this means:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>New survey submissions are being saved successfully</li>
                  <li>The database collection needs permission adjustments to display results</li>
                  <li>This is a common issue when deploying to production environments</li>
                </ul>
                <p className="mt-3"><strong>Next Steps:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Contact your database administrator to grant read permissions to the 'sessions' collection</li>
                  <li>Alternatively, check if submissions are being saved to a different collection name</li>
                </ul>
              </div>
            </div>
          )}

          {/* Controls */}
          {!showDatabaseMessage && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-white">Show:</span>
                <select 
                  value={limit} 
                  onChange={(e) => setLimit(parseInt(e.target.value))}
                  className="bg-gray-800 border border-gray-600 rounded px-3 py-1 text-white"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-white">per page</span>
              </div>
              
              <div className="text-white">
                Total: {total} submissions
              </div>
            </div>
          )}

          {/* Sessions Table */}
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Phone</th>
                      <th className="px-4 py-3 text-left">Current Students</th>
                      <th className="px-4 py-3 text-left">Monthly Rate</th>
                      <th className="px-4 py-3 text-left">Goal Students</th>
                      <th className="px-4 py-3 text-left">Pain Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr key={session._id} className="border-b border-gray-700">
                        <td className="px-4 py-3 text-sm">{formatDate(session.timestamp)}</td>
                        <td className="px-4 py-3">{session.name || 'N/A'}</td>
                        <td className="px-4 py-3">{session.phone || 'N/A'}</td>
                        <td className="px-4 py-3">{session.current_students || 'N/A'}</td>
                        <td className="px-4 py-3">${session.monthly_rate || 'N/A'}</td>
                        <td className="px-4 py-3">{session.goal_students || 'N/A'}</td>
                        <td className="px-4 py-3 max-w-xs truncate" title={session.pain_points}>
                          {session.pain_points || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-4 mt-6">
              <Button 
                onClick={() => setPage(page - 1)} 
                disabled={page === 1}
                className="bg-gray-700 hover:bg-gray-600"
              >
                Previous
              </Button>
              
              <span className="text-white">
                Page {page} of {totalPages}
              </span>
              
              <Button 
                onClick={() => setPage(page + 1)} 
                disabled={page === totalPages}
                className="bg-gray-700 hover:bg-gray-600"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Route handling
  if (isAdminLogin) {
    return <AdminLogin />;
  }

  if (isAdminMode && adminToken) {
    return <AdminDashboard />;
  }

  if (isAdminMode && !adminToken) {
    window.location.href = '/admin-login';
    return null;
  }

  const FinalStep = () => {
    const [choiceSelected, setChoiceSelected] = useState('');
    const roi = calculateROI();
    const pricingSectionRef = React.useRef(null);
    const rejectionSectionRef = React.useRef(null);

    const handleChoice1Click = () => {
      setChoiceSelected('stay');
      setTimeout(() => {
        if (rejectionSectionRef.current) {
          rejectionSectionRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    };

    const handleChoice2Click = () => {
      setChoiceSelected('change');
      setTimeout(() => {
        if (pricingSectionRef.current) {
          pricingSectionRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    };
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-white mb-4">
            You're One Decision Away From Success
          </h2>
          <p className="text-xl text-white">
            {sessionData.name}, you have two choices right now...
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card 
            className={`bg-red-900/20 border-red-500 cursor-pointer transition-all ${choiceSelected === 'stay' ? 'ring-2 ring-red-500' : ''}`}
            onClick={handleChoice1Click}
          >
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Choice #1: Keep Doing What You're Doing</h3>
              <p className="text-white">
                Stay at {sessionData.current_students} students, keep struggling with marketing,
                and expect the same results you've been getting.
              </p>
            </CardContent>
          </Card>

          <Card 
            className={`bg-green-900/20 border-green-500 cursor-pointer transition-all ${choiceSelected === 'change' ? 'ring-2 ring-green-500' : ''}`}
            onClick={handleChoice2Click}
          >
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-4">Choice #2: Make a Change</h3>
              <p className="text-white">
                Get our proven system and expect to reach {sessionData.goal_students} students
                with an extra <strong className="text-yellow-400">${roi.yearlyIncrease.toLocaleString()}*</strong> this year.
              </p>
            </CardContent>
          </Card>
        </div>

        {choiceSelected === 'stay' && (
          <div ref={rejectionSectionRef}>
            <Card className="bg-red-900/30 border border-red-500">
              <CardContent className="p-6 text-center">
                <p className="text-white text-lg">
                  If you're choosing to stay where you are, then you're probably not a good fit for the Student Magnet, but we wish you success nonetheless!
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {choiceSelected === 'change' && (
          <div ref={pricingSectionRef}>
            <Card className="bg-gradient-to-r from-primary/30 to-yellow-500/30 border-primary">
              <CardContent className="p-8 text-center">
                <div className="mb-4">
                  <p className="text-2xl font-bold text-green-400 mb-2">Good choice!</p>
                  <p className="text-xl text-white">Here's how to get started</p>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4">Student Magnet+ Program</h3>
                <div className="mb-6">
                  <p className="text-white line-through text-xl">$17,500 Value</p>
                  <p className="text-white line-through text-2xl">$10,000/year Regular Price</p>
                  <p className="text-5xl font-bold mb-2" style={{color: '#52aef2'}}>$997 Today</p>
                  <p className="text-3xl font-bold" style={{color: '#52aef2'}}>then just $297/month</p>
                  <p className="text-white">Special Limited Time Pricing - Today Only</p>
                  <CountdownTimer />
                </div>
                
                <div className="text-left mb-6">
                  <h4 className="text-white font-semibold mb-3">Everything You Get:</h4>
                  <ul className="text-white space-y-2">
                    <li>✓ Complete Student Magnet+ System</li>
                    <li>✓ Effective Ad Account Setup ($3,500 value)</li>
                    <li>✓ Follow-Up Ninja Automation ($3,500 value)</li>
                    <li>✓ Paid Trial Playbook ($2,500 value)</li>
                    <li>✓ 2x Weekly Coaching Calls ($5,000 value)</li>
                    <li>✓ Private School Owner Community ($3,000 value)</li>
                    <li>✓ 30-Day Money Back Guarantee</li>
                  </ul>
                </div>

                <div className="bg-black/40 p-4 rounded-lg mb-6">
                  <p className="text-yellow-400 font-bold text-lg">
                    Your ROI: If this system gets you just {Math.ceil(955 / parseInt(sessionData.monthly_rate || 155))} new students, it pays for itself.
                  </p>
                </div>

                <Button 
                  size="lg" 
                  className="w-full text-xl py-10 px-6 min-h-[100px] flex flex-col items-center justify-center space-y-2"
                  style={{backgroundColor: '#52aef2'}}
                  onClick={() => window.open('https://thefollowupninja.com/order-form', '_blank')}
                >
                  <span className="text-2xl font-bold leading-none">Enroll Now</span>
                  <span className="text-sm font-normal leading-none">Transform My School</span>
                  <ArrowRight className="h-6 w-6" />
                </Button>
                
                <p className="text-white text-sm mt-4">
                  30-Day Money Back Guarantee • Secure Checkout • Start Today
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  const steps = [
    () => <IntroStep nextStep={nextStep} />,
    () => <CurrentSituationStep localInputs={localInputs} updateLocalInput={updateLocalInput} nextStep={nextStep} />,
    () => <PainPointsStep sessionData={sessionData} localInputs={localInputs} updateLocalInput={updateLocalInput} nextStep={nextStep} />,
    () => <GoalsStep localInputs={localInputs} updateLocalInput={updateLocalInput} nextStep={nextStep} />,
    BucketsStep,
    () => <ContactStep sessionData={sessionData} localInputs={localInputs} updateLocalInput={updateLocalInput} nextStep={nextStep} getPersonalizedMessage={getPersonalizedMessage} />,
    SolutionStep,
    FinalStep
  ];

  const CurrentStepComponent = steps[currentStep];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl" style={{ paddingTop: '40px' }}>
        <CurrentStepComponent />
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6" style={{ marginTop: '400px' }}>
        <div className="container mx-auto px-4 text-center">
          <div className="mb-4">
            <p className="text-white text-sm mb-2">
              © 2025 Student Magnet Systems • Helping Martial Arts Schools Grow Since 2018
            </p>
            <a 
              href="https://www.getkaratestudents.com/earnings-disclaimer-3915" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline text-sm"
            >
              Earnings Disclaimer
            </a>
          </div>
          
          <div className="text-gray-400 text-xs leading-relaxed max-w-6xl mx-auto">
            <p>
              This website is operated and maintained by GetFullClasses.com Use of the website is governed by its Terms Of Service and Privacy Policy.
            </p>
            <br />
            <p>
              GetFullClasses.com is a marketing education and training company. We do not sell a business opportunity, "get rich quick" program or money-making system. We believe, with education, individuals can be better prepared to make investment decisions, but we do not guarantee success in our training. We do not make earnings claims, efforts claims, or claims that our training will make you any money. All material is intellectual property and protected by copyright. Any duplication, reproduction, or distribution is strictly prohibited. Please see our Disclaimer for important details.
            </p>
            <br />
            <p>
              Investing in your business development carries risk and it is possible to lose some or all of your money. The training provided is general in nature, and some strategies may not be appropriate for all individuals or all situations. We make no representation regarding the likelihood or probability that any actual or hypothetical investment will achieve a particular outcome or perform in any predictable manner.
            </p>
            <br />
            <p>
              Statements and depictions are the opinions, findings, or experiences of individuals who generally have purchased education and training. Results vary, are not typical, and rely on individual effort, time, and skill, as well as unknown conditions and other factors. We do not measure earnings or financial performance. Results show that most clients who apply the training get some results. You should not, however, equate more leads and sales calls to more revenue. Further, many customers do not continue with the program, do not apply what they learn, or do attempt to apply what they learn and nonetheless have difficulty in making lead generation successful for them.
            </p>
            <br />
            <p>
              The Company may link to content or refer to content and/or services created by or provided by third parties that are not affiliated with the Company. The Company is not responsible for such content and does not endorse or approve it. The Company may provide services by or refer you to third-party businesses. Some of these businesses have common interest and ownership with the Company.
            </p>
            <br />
            <p>
              GetFullClasses.com. We use cookies to help improve, promote and protect our services. By continuing to use this site, you agree to our privacy policy and terms of use. This site is not a part of Facebook website or Facebook, Inc. This site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of META, Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;